// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspRecordCreate.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Form, Input, Spin, Row, Col, message, Card, Select, DatePicker, Typography, Space, Divider, Checkbox, Empty, Radio, FormInstance } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { SaveOutlined, AuditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  useGetVwQaqcInspFormDetails,
  useGetProjSystems,     // For id from to_pln_proj_systems
  useGetPersonnel,     // For inspector_id and witnesses
} from '../../../hooks/genericHooks';
import { fetchSqlData, insertData } from '../../../services/api';
import type {
  VwQaqcInspFormDetails,
  QaqcInspForm, // This is the target interface for saving
  ProjSystems,    // Using ProjSystems for id
  VwPersonnel,
} from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper to get unique form designs for the selection dropdown
interface QaqcFormDesignSelectItem {
  id: number; // design_id
  form_num: string;
  form_name: string;
}

const QaqcInspRecordCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedFormDesignId, setSelectedFormDesignId] = useState<number | null>(null);
  const [checklistItems, setChecklistItems] = useState<VwQaqcInspFormDetails[]>([]);
  
  // State for "Select All Pass" checkbox
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [selectAllIndeterminate, setSelectAllIndeterminate] = useState<boolean>(false);

  // --- Data Fetching ---
  const { data: allFormDetailsData, isLoading: isLoadingFormDetails } = useGetVwQaqcInspFormDetails();
  const { data: projSystemsData, isLoading: isLoadingProjSystems } = useGetProjSystems();
  const projSystems: ProjSystems[] = projSystemsData?.data || [];
  const { data: personnelData, isLoading: isLoadingPersonnel } = useGetPersonnel();
  const personnelList: VwPersonnel[] = personnelData?.data || [];

  const {
    data: selectedFormChecklistData,
    isLoading: isLoadingSelectedChecklist,
    refetch: refetchChecklistItems,
  } = useQuery<VwQaqcInspFormDetails[], Error>({
    queryKey: ['qaqcFormChecklistItemsForRecord', selectedFormDesignId],
    queryFn: async () => {
      if (!selectedFormDesignId) return [];
      const data = await fetchSqlData('vw_qaqc_insp_formdetails', 'design_id', selectedFormDesignId);
      return data as VwQaqcInspFormDetails[]; // Ensure the data conforms to the expected type for useQuery
    },
    enabled: !!selectedFormDesignId,
  });

  useEffect(() => {
    if (selectedFormDesignId) {
      // Clear previous checklist item fields from the form
      const oldItemFieldNames = checklistItems.flatMap(item => [
        `item_status_${item.field_id}`,
        `item_remarks_${item.field_id}`,
      ]);
      if (oldItemFieldNames.length > 0) {
        form.resetFields(oldItemFieldNames);
      }
      refetchChecklistItems();
    } else {
      setChecklistItems([]);
      form.resetFields(checklistItems.flatMap(item => [`item_status_${item.field_id}`, `item_remarks_${item.field_id}`]));
    }
    setSelectAllChecked(false);
    setSelectAllIndeterminate(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormDesignId]); 

  // Moved onSuccess logic into a useEffect hook
  useEffect(() => {
    if (selectedFormChecklistData) {
      const items = selectedFormChecklistData || [];
      setChecklistItems(items);
      const initialItemValues: any = {};
      items.forEach(item => {
        initialItemValues[`item_status_${item.field_id}`] = false; // Default to false (Fail) for checkbox
        initialItemValues[`item_remarks_${item.field_id}`] = '';
      });
      form.setFieldsValue(initialItemValues);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormChecklistData]); 

  // Effect to update "Select All Pass" checkbox state based on individual item selections
  useEffect(() => {
    if (!checklistItems || checklistItems.length === 0) {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(false);
      return;
    }

    const formValues = form.getFieldsValue();
    let checkedCount = 0;
    checklistItems.forEach(item => {
      if (formValues[`item_status_${item.field_id}`] === true) {
        checkedCount++;
      }
    });

    if (checkedCount === 0) {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(false);
    } else if (checkedCount === checklistItems.length) {
      setSelectAllChecked(true);
      setSelectAllIndeterminate(false);
    } else {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(true);
    }
    // Watching form.getFieldsValue() directly can cause performance issues or infinite loops if not handled carefully.
    // A more robust way might involve listening to individual field changes if performance becomes an issue.
    // For now, this approach is common for smaller forms.
  }, [checklistItems, form, form.getFieldsValue()]); // Watch form values for changes

  const uniqueFormDesignsForSelection = useMemo((): QaqcFormDesignSelectItem[] => {
    if (!allFormDetailsData?.data) return [];
    const designsMap = new Map<number, QaqcFormDesignSelectItem>();
    allFormDetailsData.data.forEach(detail => {
      if (!designsMap.has(detail.design_id)) {
        designsMap.set(detail.design_id, {
          id: detail.design_id,
          form_num: detail.form_num,
          form_name: detail.form_name,
        });
      }
    });
    return Array.from(designsMap.values()).sort((a, b) => a.form_name.localeCompare(b.form_name));
  }, [allFormDetailsData]);

  const createRecordMutation = useMutation<QaqcInspForm, Error, Partial<QaqcInspForm>>({
    mutationFn: (payload) => insertData('qaqc_insp_forms', payload),
    onSuccess: (data) => {
      message.success(`QA/QC Inspection Form (ID: ${data.id}) created successfully!`);
      queryClient.invalidateQueries({ queryKey: ['qaqcInspForms'] });
      navigate('/qaqc/inspections/forms-list'); // Adjust if your list route is different
    },
    onError: (error: any) => {
      console.error('Error creating QA/QC inspection form:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to create record.';
      message.error(`Error: ${errorMsg}`);
    },
  });

  const onFinish = (values: any) => {
    if (!selectedFormDesignId) {
      message.error('Please select an Inspection Form Design.');
      return;
    }
    if (checklistItems.length === 0 && selectedFormDesignId) {
      message.warning('The selected form design has no checklist items. Saving with empty checklist.'); // Corrected from .warn
    }

    const formFieldValues: boolean[] = [];
    const formFieldRemarks: (string | null)[] = [];

    checklistItems.forEach(item => {
      const statusIsTrue = values[`item_status_${item.field_id}`]; // This will be true (Pass) or false (Fail)
      const remarks = values[`item_remarks_${item.field_id}`] || null;

      formFieldValues.push(!!statusIsTrue);
      formFieldRemarks.push(remarks);
    });

    const payload: Partial<QaqcInspForm> = {
      form_id: selectedFormDesignId,
      sys_id: values.sys_id, // This is a string from ProjSystems
      insp_date: values.insp_date ? values.insp_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      insp_place: values.insp_place || null,
      inspector_id: values.inspector_id,
      form_field_values: formFieldValues.length > 0 ? formFieldValues : null,
      form_field_remarks: formFieldRemarks.length > 0 ? formFieldRemarks : null,
      form_result: values.overall_form_result === 'Pass', // Directly from the new radio button
      form_notes: values.form_notes || null,
      witness1: values.witness1 || null,
      witness2: values.witness2 || null,
      // created_by would be set by backend or from logged-in user context
    };

    createRecordMutation.mutate(payload);
  };

  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;
    setSelectAllChecked(isChecked);
    setSelectAllIndeterminate(false);

    if (!checklistItems) return;

    const updatedValues: { [key: string]: boolean } = {};
    checklistItems.forEach(item => {
      // When "Select All" is checked, all items are Pass (true).
      // When "Select All" is unchecked, all items are Fail (false).
      updatedValues[`item_status_${item.field_id}`] = isChecked;
    });
    form.setFieldsValue(updatedValues);
  };

  const isLoading = isLoadingFormDetails || isLoadingProjSystems || isLoadingPersonnel || createRecordMutation.isPending;

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} type="text" />
          <AuditOutlined />
          <Title level={4} style={{ margin: 0 }}>Create New QA/QC Inspection</Title>
        </Space>
      }
    >
      <Spin spinning={isLoading} tip={createRecordMutation.isPending ? "Saving Inspection..." : "Loading data..."}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          initialValues={{ insp_date: dayjs() }}
        >
          {/* Form Selection Moved to Top */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Select Form Design"
                name="form_design_selector" 
                rules={[{ required: true, message: 'Please select a form design!' }]}
              >
                <Select
                  placeholder="Select Form Design"
                  loading={isLoadingFormDetails}
                  onChange={(value) => {
                    setSelectedFormDesignId(value as number);
                  }}
                  value={selectedFormDesignId}
                  showSearch
                  filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {uniqueFormDesignsForSelection.map(design => (
                    <Option key={design.id} value={design.id}>{`${design.form_name} (${design.form_num})`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="sys_id" label="Project System ID" rules={[{ required: true, message: 'Please select a project system!' }]}>
                <Select
                  placeholder="Select Project System"
                  loading={isLoadingProjSystems}
                  showSearch
                  filterOption={(input, option) => 
                    (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase()) ||
                    (option?.value as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {projSystems.map(system => (
                    <Option key={system.sys_id} value={system.sys_id}>{`${system.sys_name} (${system.sys_id})`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="insp_date" label="Inspection Date" rules={[{ required: true, message: 'Please select inspection date!' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="insp_place" label="Location / Area">
                <Input placeholder="Enter Inspection Place" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="inspector_id" label="Inspector" rules={[{ required: true, message: 'Please select an inspector!' }]}>
                <Select placeholder="Select Inspector" loading={isLoadingPersonnel} showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="witness1" label="Witness 1 (Optional)">
                <Select placeholder="Select Witness 1" loading={isLoadingPersonnel} allowClear showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="witness2" label="Witness 2 (Optional)">
                <Select placeholder="Select Witness 2" loading={isLoadingPersonnel} allowClear showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Checklist items will appear below once a form design is selected */}
          {selectedFormDesignId && checklistItems.length > 0 && (
            <Row align="middle" style={{ marginTop: 16, marginBottom: 12 }}>
              <Col flex="auto">
                <Divider orientation="left" plain style={{ margin: 0 }}>Check Items</Divider>
              </Col>
              <Col style={{ paddingLeft: 16 }}>
                <Checkbox checked={selectAllChecked} indeterminate={selectAllIndeterminate} onChange={handleSelectAllChange}>
                  Select All Pass
                </Checkbox>
              </Col>
            </Row>
          )}

          {isLoadingSelectedChecklist && <Spin tip="Loading checklist items..." style={{ display: 'block', marginTop: 16 }} />}

          {!selectedFormDesignId && <Empty description="Select a Form Design to see check items" style={{ marginTop: 16 }} />}

          {selectedFormDesignId && !isLoadingSelectedChecklist && checklistItems.length > 0 && (
            checklistItems.map((item, index) => (
              <Row key={item.field_id} gutter={16} align="middle" style={{ marginBottom: '8px' }}> {/* Reduced margin as per previous request */}
                <Col xs={24} sm={24} md={12}> {/* Check Item Text */}
                  <Text>{index + 1}. {item.check_item}</Text>
                </Col>
                <Col xs={12} sm={6} md={4}> {/* Status Checkbox */}
                  <Form.Item
                    name={`item_status_${item.field_id}`}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>Pass</Checkbox>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={18} md={8}> {/* Remarks */}
                  <Form.Item
                    name={`item_remarks_${item.field_id}`}
                    noStyle
                  >
                    <TextArea rows={1} placeholder="Remarks" />
                  </Form.Item>
                </Col>
              </Row>
            ))
          )}

          {!isLoadingSelectedChecklist && selectedFormDesignId && checklistItems.length === 0 && (
            <Empty description="Selected form design does not have any checklist items." style={{ marginTop: 16 }} />
          )}

          <Divider style={{ marginTop: 24, marginBottom: 12 }}>Summary</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="form_notes" label="Overall Notes" labelCol={{sm: 24, md: 5}} wrapperCol={{sm:24, md:19}}>
                <TextArea rows={3} placeholder="Enter any overall notes for the inspection" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item 
                name="overall_form_result" 
                label="Overall Result" 
                rules={[{ required: true, message: 'Please select the overall result!' }]}
                labelCol={{sm: 24, md: 5}} wrapperCol={{sm:24, md:19}}>
                <Radio.Group>
                  <Radio value="Pass">Pass</Radio>
                  <Radio value="Fail">Fail</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            wrapperCol={{ offset: 8, span: 16 }} // Offset by labelCol span
            style={{ textAlign: 'right', marginTop: '24px' }}
          >
            <Space>
              <Button onClick={() => navigate('/qaqc/inspections/forms-list')}> {/* Adjust nav path */}
                Cancel
              </Button>
              <Button
                action="add"
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createRecordMutation.isPending} // Ensure this evaluates to boolean
                disabled={!selectedFormDesignId || isLoadingSelectedChecklist}
              >
                Save Inspection
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default QaqcInspRecordCreate;